import { useCallback, useEffect, useState } from "react";
import { getApiErrorMessage } from "../../../lib/result-codes";
import {
  getRewardHistoryList,
  getRewardHistoryStatistic,
  resendReward,
  type RewardHistoryStatistic,
} from "../api/reward-history.api";
import {
  type RewardHistoryFilter,
  type RewardHistoryItem,
  transformAPIItemToLegacy,
} from "../types/RewardHistory";

const INITIAL_FILTER: RewardHistoryFilter = {
  search: "",
  userSearch: "",
  rewardSearch: "",
  status: "",
};
const ITEMS_PER_PAGE = 20;

export const useRewardHistory = () => {
  const [rewardHistory, setRewardHistory] = useState<RewardHistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [filter, setFilterState] = useState<RewardHistoryFilter>(INITIAL_FILTER);

  const refreshCurrentPage = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getRewardHistoryList({
        filter: {
          user_name: filter.userSearch || filter.search || undefined,
          reward_name: filter.rewardSearch || filter.search || undefined,
          redemption_status:
            filter.status === "redeemed" ||
            filter.status === "used" ||
            filter.status === "pending" ||
            filter.status === "failed"
              ? filter.status
              : undefined,
          date_from: filter.dateFrom || undefined,
          date_to: filter.dateTo || undefined,
        },
        pagination: { index: currentPage - 1, size: ITEMS_PER_PAGE },
      });
      setRewardHistory(response.data.map(transformAPIItemToLegacy));
      setTotalCount(response.pagination.count);
    } catch (loadError: unknown) {
      setError(getApiErrorMessage(loadError, "Failed to load reward history"));
      setRewardHistory([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filter]);

  useEffect(() => {
    void refreshCurrentPage();
  }, [refreshCurrentPage]);

  const setFilter = useCallback((nextFilter: RewardHistoryFilter) => {
    setFilterState(nextFilter);
    setCurrentPage(1);
  }, []);
  const refreshRewardHistory = useCallback(async () => {
    await refreshCurrentPage();
  }, [refreshCurrentPage]);
  const resend = useCallback(
    async (userCouponId: string) => {
      const result = await resendReward(userCouponId);
      await refreshCurrentPage();
      return result;
    },
    [refreshCurrentPage],
  );
  const getStatistic = useCallback(
    async (): Promise<RewardHistoryStatistic> => getRewardHistoryStatistic(),
    [],
  );

  return {
    rewardHistory,
    loading,
    error,
    currentPage,
    totalPages: Math.ceil(totalCount / ITEMS_PER_PAGE),
    totalCount,
    itemsPerPage: ITEMS_PER_PAGE,
    filter,
    setPage: setCurrentPage,
    setFilter,
    refreshRewardHistory,
    refreshCurrentPage,
    resendReward: resend,
    getStatistic,
  };
};
